import { verificationsApi } from '../../../data/verificationsApi';
import type {
  EstimateEvaluateRequest,
  EstimateEvaluationView,
  ExtractProceduresFromNoteRequest,
  ExtractProceduresFromNoteResponse,
  PatientResponsibilityCalcRequest,
  PatientResponsibilityCalcResponse,
  ReimbursementCalcRequest,
  ReimbursementCalcResult,
} from '../../../data';

/**
 * Estimate feature endpoints, injected into the single verifications base api (spec 006).
 *
 * `evaluateEstimate` is the stateless `POST /verification/estimate/evaluate` (FR-046): it sends the
 * working procedure set + the Encounter-sourced context (admission type / admit source) and returns
 * `{ matches, alerts, bypassSuggestion, authorizationWarnings }`. Read-only — never persisted; the
 * result hydrates the slice's transient `evaluation` and refreshes `current.authorizationWarnings`
 * via a surgical matcher. The client holds no rule codes/thresholds (FR-047).
 *
 * `calculatePatientResponsibility` is the stateless `POST /verification/patient-responsibility/calculate`
 * (spec 024): it sends the working calculator inputs (benefits + contribution + secondary-ins + self-pay)
 * to the Verifications BFF, which proxies to the Calculators `/PatientResponsibility` (spec 023) and
 * returns the computed `amountDue`. The client holds no EPR math and never calls Calculators directly.
 *
 * `calculateReimbursement` is the stateless `POST /verification/estimate/reimbursement/calculate`
 * (spec 027): it sends the working procedures to the Verifications BFF, which assembles the request and
 * proxies to the Calculators `/api/Reimbursement/calculate`, returning per-procedure reimbursement. The
 * client holds no reimbursement math and never calls Calculators directly (FR-004/SC-004).
 *
 * `extractProceduresFromNote` is the stateless `POST /verification/procedures/extract-from-note`
 * (spec 028 US5): the manual Extract button on the appointment-note banner sends `{ caseId, apptNoteText }`
 * to the Verifications BFF, which runs the whole AI extraction pipeline (model + lookup + rules + gate +
 * persist) and returns an outcome envelope. On success it invalidates the Verification tag so the
 * procedures + Diagnosis 1–4 reload. The client holds no extraction/rule/model logic (FR-012).
 */
export const estimateEndpoints = verificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    evaluateEstimate: builder.mutation<EstimateEvaluationView, EstimateEvaluateRequest>({
      query: (body) => ({
        url: '/verification/estimate/evaluate',
        method: 'POST',
        body,
      }),
    }),
    calculatePatientResponsibility: builder.mutation<
      PatientResponsibilityCalcResponse,
      PatientResponsibilityCalcRequest
    >({
      query: (body) => ({
        url: '/verification/patient-responsibility/calculate',
        method: 'POST',
        body,
      }),
    }),
    calculateReimbursement: builder.mutation<ReimbursementCalcResult, ReimbursementCalcRequest>({
      query: (body) => ({
        url: '/verification/estimate/reimbursement/calculate',
        method: 'POST',
        body,
      }),
    }),
    extractProceduresFromNote: builder.mutation<
      ExtractProceduresFromNoteResponse,
      ExtractProceduresFromNoteRequest
    >({
      query: (body) => ({
        url: '/verification/procedures/extract-from-note',
        method: 'POST',
        body,
      }),
      // No tag invalidation: the response carries the applied procedures + Diagnosis 1–4, and the hook
      // applies them to the Estimate slice in place. A full case refetch here would clobber other unsaved
      // edits (e.g. a pending Check Eligibility benefit load on the Coverage tab).
    }),
  }),
  overrideExisting: false,
});

export const {
  useEvaluateEstimateMutation,
  useCalculatePatientResponsibilityMutation,
  useCalculateReimbursementMutation,
  useExtractProceduresFromNoteMutation,
} = estimateEndpoints;
